import React, { useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

import AddressSearch from "../../AddressSearch";
import HeaderAuth from "../../components/common/HeaderAuth";
import SiteFooter from "../../components/common/SiteFooter";
import logo from "../../assets/logo-white-bg.png";
import "./Demenagement.css";
import { trackEvent } from "../../utils/analytics";

export { trackEvent } from "../../utils/analytics";

const CANONICAL_ORIGIN = "https://etatdulieu.com";
export const DEMENAGEMENT_DATE_MODIFIED = "2026-06-28";

export const siloLinks = [
  {
    to: "/outils/lettre-preavis-location",
    title: "Lettre de préavis location",
    text: "Générez une lettre claire, prête à copier ou imprimer.",
  },
  {
    to: "/guides/preavis-location-1-mois-ou-3-mois",
    title: "Préavis : 1 mois ou 3 mois ?",
    text: "Comprendre les cas les plus fréquents avant d'envoyer son congé.",
  },
  {
    to: "/checklists/checklist-demenagement-pdf",
    title: "Checklist déménagement",
    text: "Une liste imprimable pour ne rien oublier avant et après le jour J.",
  },
  {
    to: "/checklists/checklist-visite-logement",
    title: "Checklist visite logement",
    text: "Les points à vérifier pendant une visite de location ou d'achat.",
  },
];

function openAuthModal(mode) {
  try {
    window.dispatchEvent(new CustomEvent("open-auth-modal", { detail: { mode } }));
  } catch {}
}

export function makeBreadcrumbs(items) {
  return [
    { name: "Accueil", path: "/" },
    ...items,
  ];
}

function breadcrumbJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${CANONICAL_ORIGIN}${item.path}`,
    })),
  };
}

const publisherJsonLd = {
  "@type": "Organization",
  name: "État du Lieu",
  url: CANONICAL_ORIGIN,
  logo: `${CANONICAL_ORIGIN}/logo-blue-bg.png`,
};

export function itemListJsonLd({ name, description, path, items }) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name,
    description,
    url: `${CANONICAL_ORIGIN}${path}`,
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name || item.title,
      url: `${CANONICAL_ORIGIN}${item.path || item.to || path}`,
    })),
  };
}

export function articleJsonLd({ headline, description, path, keywords = [] }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline,
    description,
    inLanguage: "fr-FR",
    mainEntityOfPage: `${CANONICAL_ORIGIN}${path}`,
    dateModified: DEMENAGEMENT_DATE_MODIFIED,
    datePublished: DEMENAGEMENT_DATE_MODIFIED,
    author: publisherJsonLd,
    publisher: publisherJsonLd,
    keywords,
  };
}

export function SeoHead({
  title,
  description,
  path,
  breadcrumbs,
  jsonLd = [],
  includeDates = true,
  ogType = "website",
}) {
  const pageUrl = `${CANONICAL_ORIGIN}${path}`;
  const allJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: title,
      description,
      url: pageUrl,
      inLanguage: "fr-FR",
      ...(includeDates ? { dateModified: DEMENAGEMENT_DATE_MODIFIED } : {}),
      isPartOf: {
        "@type": "WebSite",
        name: "État du Lieu",
        url: CANONICAL_ORIGIN,
      },
      publisher: publisherJsonLd,
    },
    breadcrumbJsonLd(breadcrumbs),
    ...jsonLd,
  ];

  return (
    <Helmet prioritizeSeoTags>
      <link rel="canonical" href={pageUrl} />
      <meta name="robots" content="index,follow,max-image-preview:large" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="État du Lieu" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:image" content={`${CANONICAL_ORIGIN}/logo-blue-bg.png`} />
      <meta property="og:image:alt" content="État du Lieu - rapport d'adresse en France" />
      <meta name="twitter:image" content={`${CANONICAL_ORIGIN}/logo-blue-bg.png`} />
      <meta property="og:locale" content="fr_FR" />
      <meta name="twitter:card" content="summary_large_image" />
      {allJsonLd.map((data, index) => (
        <script type="application/ld+json" key={index}>
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
}

export function DemenagementLayout({
  children,
  title,
  description,
  path,
  breadcrumbs,
  jsonLd,
  label = "Déménagement",
  printMode = "page",
  includeDates = true,
  ogType = "website",
  navigation = "demenagement",
}) {
  return (
    <div className="move-page" data-print-mode={printMode}>
      <SeoHead
        title={title}
        description={description}
        path={path}
        breadcrumbs={breadcrumbs}
        jsonLd={jsonLd}
        includeDates={includeDates}
        ogType={ogType}
      />
      <a className="move-skip-link" href="#main">
        Aller au contenu
      </a>
      <div className="move-shell">
        <HeaderAuth
          floating
          top={12}
          right={12}
          onOpenLogin={() => openAuthModal("login")}
          onOpenSignup={() => openAuthModal("signup")}
        />
        <nav className="move-topbar no-print" aria-label="Navigation principale">
          <Link to="/" className="move-brand">
            <img src={logo} alt="" aria-hidden="true" />
            État du Lieu
          </Link>
          <div className="move-toplinks">
            {navigation === "guides" ? (
              <>
                <Link to="/guides">Guides pratiques</Link>
                <Link to="/checklists/checklist-visite-logement">Checklist visite</Link>
                <Link to="/demenagement">Déménagement</Link>
              </>
            ) : (
              <>
                <Link to="/demenagement">Déménagement</Link>
                <Link to="/outils/lettre-preavis-location">Lettre de préavis</Link>
                <Link to="/guides/preavis-location-1-mois-ou-3-mois">Guide préavis</Link>
                <Link to="/checklists/checklist-demenagement-pdf">Checklist déménagement</Link>
                <Link to="/checklists/checklist-visite-logement">Checklist visite</Link>
              </>
            )}
          </div>
        </nav>
        <main id="main" className="move-main">
          <div className="move-kicker">{label}</div>
          {children}
        </main>
        <div className="move-footer-wrap no-print">
          <SiteFooter />
        </div>
      </div>
    </div>
  );
}

export function Breadcrumbs({ items }) {
  return (
    <nav className="move-breadcrumbs no-print" aria-label="Fil d'Ariane">
      {items.map((item, index) => (
        <React.Fragment key={item.path}>
          {index > 0 && <span aria-hidden="true">/</span>}
          {index === items.length - 1 ? (
            <span aria-current="page">{item.name}</span>
          ) : (
            <Link to={item.path}>{item.name}</Link>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

export function DictimmoCta({
  title,
  text,
  buttonLabel = "Vérifier ma future adresse",
  eventName,
  eventData = {},
}) {
  const trackedRef = useRef(false);
  const track = () => {
    if (!eventName || trackedRef.current) return;
    trackedRef.current = true;
    trackEvent(eventName, { location: "address_cta", ...eventData });
  };

  return (
    <section className="move-address-cta no-print" aria-label={title}>
      <div>
        <h2>{title}</h2>
        <p>{text}</p>
        <Link className="move-btn move-btn-primary" to="/#search" onClick={track}>
          {buttonLabel}
        </Link>
      </div>
      <div className="move-address-search" onFocusCapture={track} onMouseDownCapture={track}>
        <AddressSearch />
      </div>
    </section>
  );
}

export function SourceList({ sources }) {
  return (
    <section className="move-sources no-print" aria-labelledby="sources-title">
      <h2 id="sources-title">Sources officielles et utiles</h2>
      <ul>
        {sources.map((source) => (
          <li key={source.href}>
            <a href={source.href} target="_blank" rel="noopener noreferrer">
              {source.label}
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function InternalLinks({ currentPath }) {
  return (
    <section className="move-internal-links no-print" aria-labelledby="move-tools-title">
      <h2 id="move-tools-title">Autres outils pour préparer votre déménagement</h2>
      <div className="move-card-grid move-card-grid-compact">
        {siloLinks
          .filter((link) => link.to !== currentPath)
          .map((link) => (
            <Link className="move-card-link" to={link.to} key={link.to}>
              <span>{link.title}</span>
              <small>{link.text}</small>
            </Link>
          ))}
      </div>
    </section>
  );
}

export function InteractiveChecklist({ title, intro, sections, printEventName }) {
  const allIds = useMemo(
    () => sections.flatMap((section) => section.items.map((item) => item.id)),
    [sections]
  );
  const [checked, setChecked] = useState({});
  const checkedCount = allIds.filter((id) => checked[id]).length;
  const allChecked = allIds.length > 0 && checkedCount === allIds.length;

  const toggleOne = (id) => {
    setChecked((current) => ({ ...current, [id]: !current[id] }));
  };

  const toggleAll = () => {
    if (allChecked) {
      setChecked({});
      return;
    }
    setChecked(
      allIds.reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, {})
    );
  };

  const printChecklist = () => {
    trackEvent(printEventName, { checked: checkedCount, total: allIds.length });
    window.print();
  };

  return (
    <section className="move-checklist-page">
      <div className="move-checklist-toolbar no-print">
        <div>
          <h2>{title}</h2>
          <p>{intro}</p>
          <div className="move-progress">
            {checkedCount} / {allIds.length} points cochés
          </div>
        </div>
        <div className="move-button-row">
          <button className="move-btn move-btn-primary" type="button" onClick={printChecklist}>
            Imprimer la checklist
          </button>
          <button className="move-btn move-btn-secondary" type="button" onClick={toggleAll}>
            {allChecked ? "Tout décocher" : "Tout cocher"}
          </button>
        </div>
      </div>

      <div className="move-checklist-panel print-document">
        <header>
          <h2>{title}</h2>
          <p>{intro}</p>
        </header>
        {sections.map((section) => (
          <section className="move-check-section" key={section.title}>
            <h2>{section.title}</h2>
            <ul className="move-check-list">
              {section.items.map((item) => (
                <li className="move-check-item" key={item.id}>
                  <input
                    id={item.id}
                    type="checkbox"
                    checked={!!checked[item.id]}
                    onChange={() => toggleOne(item.id)}
                  />
                  <label htmlFor={item.id}>{item.label}</label>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </section>
  );
}
